import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useParams, useNavigate } from "react-router-dom";
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import {
  Box,
  Container,
  Alert,
  Skeleton,
  Paper,
} from "@mui/material";
import { useExplorerDates } from "./Explorer/useExplorerDates";
import { useAuth } from "../contexts/AuthContext";
import DayRangePicker from "../components/DayRangePicker";
import { ExplorerHeader } from "./Explorer/ExplorerHeader";
import { ROUTES_NAMES } from "../components/routes";
import ExplorerExportButton from "./Explorer/ExplorerExportButton";
import { LivestreamDetailRecentList } from "./Explorer/LivestreamDetailRecentList";
import { ChannelProgramsTable } from "./Explorer/ChannelProgramsTable";
import { LivestreamProgramPeriodSection } from "./Explorer/LivestreamProgramPeriodSection";
import {
  mergeExplorerLivestreamsWithLive,
  useExplorerLiveStreamsQuery,
} from "./Explorer/explorerLiveStreams";
import {
  ProgramDetailEpisodeRowSkeleton,
  EXPLORER_PANEL_CONTENT_SX,
} from "./Explorer/ExplorerLoadingSkeletons";
import {
  LIVESTREAM_DETAIL_GC_TIME,
  LIVESTREAM_DETAIL_STALE_TIME,
} from "./Explorer/utils";
import apiService from "../services/api";
import { LivestreamStreamProgramEditDialog } from "./Explorer/LivestreamStreamProgramEditDialog";
import { getS3Url, ensureDirectS3Url } from "../utils/imageUtils";
import type { ExportConfig } from "../export";
import {
  exportAlternatives,
  LIVESTREAM_CHANNEL_REPORT_TYPE,
} from "./Explorer/explorerTabs";
function pathWithDates(path: string, startDate: string, endDate: string): string {
  const query = new URLSearchParams({ startDate, endDate });
  return `${path}?${query.toString()}`;
}

const ExplorerLivestreamChannelDetail: React.FC = () => {
  const { t } = useTranslation("explorer");
  const { channelId } = useParams<{ channelId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [programEditStreamId, setProgramEditStreamId] = useState<string | null>(
    null,
  );

  const { getDateFilterMinStartDate, getDateFilterMaxEndDate, isAdmin } =
    useAuth();
  const { startDate, endDate, setRange, withDates } = useExplorerDates();
  const currentMinDate = getDateFilterMinStartDate();
  const currentMaxDate = getDateFilterMaxEndDate();

  const liveQuery = useExplorerLiveStreamsQuery(!!channelId);

  const {
    data,
    isLoading,
    error,
    isFetching,
    isPlaceholderData,
  } = useQuery({
    queryKey: [
      "explorer-livestream-channel",
      channelId,
      startDate,
      endDate,
      "duration-v2",
      "live-segments-v3",
    ],
    queryFn: () =>
      apiService.getExplorerLivestreamChannel(channelId!, startDate, endDate),
    enabled: !!channelId,
    staleTime: LIVESTREAM_DETAIL_STALE_TIME,
    gcTime: LIVESTREAM_DETAIL_GC_TIME,
    placeholderData: keepPreviousData,
  });
  const storedLivestreams = data?.livestreams ?? [];
  const livestreams = useMemo(
    () =>
      mergeExplorerLivestreamsWithLive(
        storedLivestreams,
        liveQuery.data?.streams,
        liveQuery.isSuccess,
        { youtubeChannelId: data?.channel.channelId },
      ),
    [
      storedLivestreams,
      data?.channel.channelId,
      liveQuery.data?.streams,
      liveQuery.isSuccess,
    ],
  );

  const channelName = data?.channel.channelName;
  const exportConfig = useMemo<ExportConfig | undefined>(() => {
    if (!channelId) return undefined;
    return {
      reportType: LIVESTREAM_CHANNEL_REPORT_TYPE,
      params: { startDate, endDate, channelId },
      contextLabels: channelName ? [{ label: t("channel"), value: channelName }] : [],
      alternatives: exportAlternatives([LIVESTREAM_CHANNEL_REPORT_TYPE]),
    };
  }, [channelId, channelName, startDate, endDate, t]);

  const openProgram = (programId: string) => {
    if (!channelId) return;
    navigate(
      pathWithDates(
        generatePath(ROUTES_NAMES.EXPLORER_LIVESTREAM_CHANNEL_PROGRAM, {
          channelId,
          programId,
        }),
        startDate,
        endDate,
      ),
    );
  };

  if (isLoading && !data) {
    return (
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <ExplorerHeader
          loading
          onBack={() =>
            navigate(withDates(ROUTES_NAMES.EXPLORER_LIVESTREAMS_LIST), {
              replace: true,
            })
          }
          image={{ name: "" }}
          title=""
        >
          <DayRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={setRange}
            minDate={currentMinDate}
            maxDate={currentMaxDate}
          />
          <ExplorerExportButton config={exportConfig} />
        </ExplorerHeader>

        <LivestreamProgramPeriodSection
          channelId={channelId ?? ""}
          startDate={startDate}
          endDate={endDate}
          title={t("periodSummary")}
          forceLoading
          livestreams={[]}
          variant="channel"
        />

        <ChannelProgramsTable livestreams={[]} isLoading onRowClick={() => undefined} />

        <Box>
          <Skeleton variant="text" width={280} height={36} sx={{ mb: 2 }} />
          <Paper sx={EXPLORER_PANEL_CONTENT_SX}>
            {Array.from({ length: 5 }, (_, i) => (
              <ProgramDetailEpisodeRowSkeleton
                key={`livestream-skeleton-${i}`}
              />
            ))}
          </Paper>
        </Box>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <Alert severity="error">{t("loadChannelError")}</Alert>
      </Container>
    );
  }

  const { channel } = data;
  const totalStreams = livestreams.length;
  const showDetailContentLoading =
    (isLoading && !data) || (isFetching && isPlaceholderData);
  return (
    <Container maxWidth="xl" sx={{ pb: 4 }}>
      <ExplorerHeader
        onBack={() =>
          navigate(withDates(ROUTES_NAMES.EXPLORER_LIVESTREAMS_LIST), {
            replace: true,
          })
        }
        image={{
          name: channel.channelName,
          src: channel.imageS3Key
            ? ensureDirectS3Url(getS3Url(channel.imageS3Key))
            : undefined,
        }}
        title={channel.channelName}
      >
        <DayRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={setRange}
          minDate={currentMinDate}
          maxDate={currentMaxDate}
        />
        <ExplorerExportButton config={exportConfig} />
      </ExplorerHeader>

      <LivestreamProgramPeriodSection
        channelId={channelId!}
        startDate={startDate}
        endDate={endDate}
        title={t("periodSummary")}
        livestreams={storedLivestreams}
        variant="channel"
      />

      <ChannelProgramsTable
        livestreams={storedLivestreams}
        isLoading={showDetailContentLoading}
        onRowClick={openProgram}
      />

      <LivestreamDetailRecentList
        variant="channel"
        livestreams={livestreams}
        titleCount={totalStreams}
        totalCount={totalStreams}
        isLoading={showDetailContentLoading}
        isFetching={isFetching && !showDetailContentLoading}
        emptyMessage={t("noLivestreamsInPeriod")}
        isAdmin={isAdmin}
        onEditProgram={setProgramEditStreamId}
      />

      {isAdmin && programEditStreamId && data ? (
        <LivestreamStreamProgramEditDialog
          open
          onClose={() => setProgramEditStreamId(null)}
          streamId={programEditStreamId}
          channelId={String(data.channel._id)}
          channelName={data.channel.channelName}
          assignedProgramId={
            data.livestreams
              ?.find(
                (s) =>
                  s._id === programEditStreamId ||
                  s.livestreamId === programEditStreamId,
              )
              ?.livestreamProgramId?.trim() ?? null
          }
          assignedProgramName={
            data.livestreams?.find(
              (s) =>
                s._id === programEditStreamId ||
                s.livestreamId === programEditStreamId,
            )?.programName ?? undefined
          }
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: ["explorer-livestream-channel", channelId],
            });
          }}
        />
      ) : null}
    </Container>
  );
};

export default ExplorerLivestreamChannelDetail;
